'use client'

import { ApplyToTrialButton } from '@components/app/Button/ApplyToTrial/index.client'
import Button, { buttonClassNames } from '@components/primitives/Button'
import { Icon } from '@components/primitives/Icon'
import { Toast, type ToastProps } from '@components/primitives/Toasts/Toast'
import { classMerge } from '@components/utilities/classMerge'
import { faCalendar } from '@fortawesome/pro-regular-svg-icons/faCalendar'
import { faRectangleHistory } from '@fortawesome/pro-regular-svg-icons/faRectangleHistory'
import { faShareFromSquare } from '@fortawesome/pro-regular-svg-icons/faShareFromSquare'
import { faMoneyBill } from '@fortawesome/pro-solid-svg-icons/faMoneyBill'
import { UTM_CONTENTS, UTM_MEDIUMS, UTM_SOURCES } from '@lib/browsers/utm/types'
import DynamicRoutes from '@lib/routes/DynamicRoutes'
import absoluteUrl from '@lib/routes/helpers/absoluteUrl'
import { appendUtmParameters } from '@lib/routes/helpers/appendUtmParameters'
import { TrackingEvents } from '@lib/tracking/TrackingEvents'
import { TrackingSources } from '@lib/tracking/TrackingSources'
import type { ShareData } from '@modules/referral/helpers/handleNativeShare'
import { handleNativeShare } from '@modules/referral/helpers/handleNativeShare'
import { type ClinicalTrialForListingPage } from '@modules/trials/helpers/fragments/clinicalTrialListingFragment'
import getPreferredTrialTitle from '@modules/trials/helpers/getPreferredTrialTitle'
import type { TrialListingViewLayerOverrides } from '@modules/trials/validators/TrialListingViewLayerOverridesValidator'
import { useState } from 'react'

const getVisitCountText = (numberOfVisits: number | undefined): string => {
  if (numberOfVisits && numberOfVisits > 0) {
    return numberOfVisits.toString()
  }

  return 'Unknown'
}

const getNativeShareContext = (trial: ClinicalTrialForListingPage) => {
  const preferredTitle = getPreferredTrialTitle(trial)
  const shareData = {
    title: preferredTitle,
    url: appendUtmParameters(
      absoluteUrl(DynamicRoutes.trialNaked(trial.slug ?? ''), {
        forceProduction: true,
      }),
      {
        utm_content: UTM_CONTENTS.copyLink,
        utm_medium: UTM_MEDIUMS.referral,
        utm_source: UTM_SOURCES.trialListing,
      },
    ),
  }
  return shareData
}

async function handleNativeShareForTrialListing({
  shareData,
  showToast,
}: {
  shareData: ShareData
  showToast: (toastSettings: ToastProps | null) => void
}) {
  const trackingEvents = {
    shareAborted: TrackingEvents.TRIAL_LISTING_SHARE_ABORTED,
    shareClicked: TrackingEvents.TRIAL_LISTING_SHARE_CLICKED,
    shareSuccess: TrackingEvents.TRIAL_LISTING_SHARE_SUCCESS,
  }
  handleNativeShare({
    shareData,
    showToast,
    trackingEvents,
  })
}

export const TrialListingApplyCtaCard = ({
  trial,
  trialOverrides,
}: {
  trial: ClinicalTrialForListingPage
  trialOverrides?: TrialListingViewLayerOverrides
}) => {
  const [toastSettings, showToast] = useState<ToastProps | null>(null)

  const shareData = getNativeShareContext(trial)

  const numberOfVisits = trial.timelineElements?.reduce((acc, element) => {
    if (element.visitText) {
      const number = parseInt(element.visitText.match(/\d+/)?.[0] ?? '0')
      return acc + number
    }
    return acc
  }, 0)

  const treatmentLength = trial.timelineElements?.find(
    (element) => element.phase === 'Treatment',
  )?.duration

  let compensation = 'Varies'
  let visitCount = getVisitCountText(numberOfVisits)
  let duration = treatmentLength ?? 'Unknown'

  if (trialOverrides?.callToAction) {
    compensation = trialOverrides.callToAction.compensationDescription
    visitCount = trialOverrides.callToAction.numberOfVisitsDescription
    duration = trialOverrides.callToAction.durationDescription
  }

  return (
    <div className='sticky top-36 h-fit'>
      <div className='hidden flex-col gap-y-6 rounded-lg border border-neutral200 bg-white p-6 drop-shadow-xl md:flex'>
        <span className='text-xl font-bold text-neutral900'>
          Apply to Trial
        </span>
        <div className='flex flex-col gap-y-3'>
          <div className='flex items-center gap-x-3 text-sm text-neutral900'>
            <Icon icon={faMoneyBill} />
            <span>
              Compensation: <span className='font-bold'>{compensation}</span>
            </span>
          </div>

          <div className='flex items-center gap-x-3 text-sm text-neutral900'>
            <Icon icon={faRectangleHistory} />
            <span>
              Number of Visits: <span className='font-bold'>{visitCount}</span>
            </span>
          </div>

          {treatmentLength && (
            <div className='flex items-center gap-x-3 text-sm text-neutral900'>
              <Icon icon={faCalendar} />
              <span>
                Duration: <span className='font-bold'>{duration}</span>
              </span>
            </div>
          )}
        </div>

        <div className='flex flex-col items-center gap-y-3'>
          <ApplyToTrialButton
            buttonText='Apply Today'
            className={classMerge(
              buttonClassNames.primary.gradient,
              'h-14 w-64 rounded-lg text-white',
            )}
            source={TrackingSources.LISTING_HERO}
            trial={trial}
          />
          {trial.enrollmentCount && trial.enrollmentCount > 1 && (
            <span className='text-sm font-bold text-primary600'>
              {trial.enrollmentCount} Participants Needed
            </span>
          )}
        </div>
      </div>
      <div className='mt-4 hidden w-full drop-shadow-xl md:flex'>
        <Button
          className={classMerge(
            buttonClassNames.outline.white,
            'w-full rounded-lg bg-white text-neutral900 transition-all hover:bg-neutral100 active:bg-white',
          )}
          onClick={() =>
            handleNativeShareForTrialListing({
              shareData,
              showToast,
            })
          }
        >
          <Icon className='mr-2' icon={faShareFromSquare} />
          <span className='text-neutral900 antialiased'>Share</span>
        </Button>
      </div>
      <Toast showToast={showToast} toastSettings={toastSettings} />
    </div>
  )
}

export const TrialListingFloatingMobileCta = ({
  className,
  trial,
}: {
  className?: string
  trial: ClinicalTrialForListingPage
}) => {
  const [toastSettings, showToast] = useState<ToastProps | null>(null)
  const shareData = getNativeShareContext(trial)

  return (
    <div
      className={classMerge(
        'fixed bottom-[-100%] left-0 z-mobileTrialApplyButton flex w-full flex-wrap items-center justify-between border-t border-neutral300 bg-white p-4 md:hidden',
        className,
      )}
      style={{ transition: 'bottom 1.5s' }}
    >
      <div className='w-full'>
        <ApplyToTrialButton
          className={classMerge(
            buttonClassNames.primary.gradient,
            'rounded-lg text-white',
          )}
          source={TrackingSources.FLOATING_MOBILE_CTA}
          trial={trial}
        />
      </div>
      <div className='flex w-full justify-between pt-4'>
        {trial.enrollmentCount && trial.enrollmentCount > 1 && (
          <span className='text-xs font-bold text-primary600'>
            {trial.enrollmentCount} Participants Needed
          </span>
        )}
        <button
          className='text-xs font-bold text-neutral900 underline'
          onClick={() =>
            handleNativeShareForTrialListing({
              shareData,
              showToast,
            })
          }
        >
          Share Trial
        </button>
      </div>
      <Toast showToast={showToast} toastSettings={toastSettings} />
    </div>
  )
}
